import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Twitter, Youtube, Mail, Phone } from "lucide-react";
import { BRAND, NAV } from "../../lib/data";
import { subscribeNewsletter } from "../../lib/api";
import { toast } from "sonner";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("You're subscribed.", {
                description: "Expect our monthly insights in your inbox.",
            });
            setEmail("");
        } catch (err) {
            toast.error("Couldn't subscribe.", {
                description: "Please try again in a moment.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer
            data-testid="site-footer"
            className="relative border-t border-white/5 bg-ink-950 mt-32"
        >
            <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
            <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 pt-20 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <Link to="/" className="flex items-center gap-3 mb-6">
                            <img src={BRAND.logo} alt={BRAND.name} className="h-16 w-16" />
                            <div className="flex flex-col">
                                <span className="font-display text-lg font-bold">
                                    Netfotech Solutions
                                </span>
                                <span className="text-[11px] text-zinc-500 uppercase tracking-[0.2em]">
                                    {BRAND.tagline}
                                </span>
                            </div>
                        </Link>
                        <p className="text-zinc-400 text-[15px] leading-relaxed max-w-md">
                            We aspire, design and execute scalable  AI driven
                            innovation and secure digital ecosystems for
                            enterprises shaping the next decade.
                        </p>

                        <form
                            onSubmit={onSubscribe}
                            data-testid="newsletter-form"
                            className="mt-7 flex flex-col lg:flex-row items-stretch lg:items-center gap-2 lg:gap-0 max-w-md rounded-2xl lg:rounded-full glass p-2 lg:p-1.5 tracing-beam"
                        >
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="newsletter-input"
                                placeholder="your@email.com"
                                className="flex-1 bg-transparent px-5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="newsletter-submit"
                                className="inline-flex items-center justify-center gap-1.5 rounded-xl lg:rounded-full bg-brand-linear px-5 py-3 lg:py-2.5 text-sm font-medium text-white hover:shadow-glow-brand transition-all"
                            >
                                {loading ? "…" : "Subscribe"}
                                <ArrowUpRight className="h-4 w-4" />
                            </button>
                        </form>
                    </div>

                    <div className="md:col-span-3">
                        <div className="text-[11px] text-brand-green uppercase tracking-[0.2em] mb-4">
                            Explore
                        </div>
                        <ul className="space-y-2.5">
                            {NAV.map((item) => (
                                <li key={item.to}>
                                    <Link
                                        to={item.to}
                                        className="text-[14px] text-zinc-400 hover:text-white transition-colors"
                                        data-testid={`footer-link-${item.label
                                            .toLowerCase()
                                            .replace(/\s|&/g, "-")
                                            .replace(/--+/g, "-")}`}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="text-[11px] text-brand-green uppercase tracking-[0.2em] mb-4">
                            Reach out
                        </div>
                        <a
                            href={`mailto:${BRAND.email}`}
                            className="flex items-center gap-3 text-zinc-300 hover:text-white transition mb-3"
                        >
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full glass">
                                <Mail className="h-4 w-4 text-brand-blue" />
                            </span>
                            {BRAND.email}
                        </a>
                        <a
                            href={`tel:${BRAND.phone}`}
                            className="flex items-center gap-3 text-zinc-300 hover:text-white transition mb-6"
                        >
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full glass">
                                <Phone className="h-4 w-4 text-brand-green" />
                            </span>
                            {BRAND.phone}
                        </a>
                        <div className="flex items-center gap-2.5">
                            {[
                                { icon: Linkedin, href: BRAND.social.linkedin, label: "LinkedIn" },
                                { icon: Twitter, href: BRAND.social.twitter, label: "Twitter" },
                                { icon: Youtube, href: BRAND.social.youtube, label: "YouTube" },
                            ].map(({ icon: Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={label}
                                    data-testid={`footer-social-${label.toLowerCase()}`}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full glass brand-border-hover"
                                >
                                    <Icon className="h-4 w-4 text-zinc-300" />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="text-[12.5px] text-zinc-500">
                        © {new Date().getFullYear()} Netfotech Solutions. All rights reserved.
                    </p>
                    <nav
                        aria-label="Legal"
                        data-testid="footer-legal-links"
                        className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px]"
                    >
                        <Link
                            to="/privacy"
                            data-testid="footer-link-privacy"
                            className="text-zinc-400 hover:text-white transition-colors"
                        >
                            Privacy Policy
                        </Link>
                        <span className="text-zinc-700">·</span>
                        <Link
                            to="/cookies"
                            data-testid="footer-link-cookies"
                            className="text-zinc-400 hover:text-white transition-colors"
                        >
                            Cookie Policy
                        </Link>
                        <span className="text-zinc-700">·</span>
                        <Link
                            to="/terms"
                            data-testid="footer-link-terms"
                            className="text-zinc-400 hover:text-white transition-colors"
                        >
                            Terms &amp; Conditions
                        </Link>
                    </nav>
                    <p className="text-[12.5px] text-zinc-500">
                        Aspire. Design. Execute.
                    </p>
                </div>
            </div>
        </footer>
    );
}
